import type { PropsWithChildren, ReactNode } from "react";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Card, Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, Text, Box } from "@similarweb/ui-components/foundations";

interface ITitleContentProps {
    subtitle?: ReactNode;
    title: ReactNode;
    tooltip?: ReactNode;
}

const TitleContent = ({ subtitle, title, tooltip }: ITitleContentProps) => (
    <Stack gap="4px">
        <Flex
            gap="8px"
            alignItems="center"
        >
            <Text
                fontVariant="h6"
                color={colorsPalettes.carbon[500]}
                data-automation="widget-card-title"
            >
                {title}
            </Text>
            {tooltip ? (
                <Tooltip content={tooltip}>
                    <SWReactIcons
                        iconName="info"
                        size="xs"
                    />
                </Tooltip>
            ) : null}
        </Flex>
        {subtitle ? (
            <Text
                fontVariant="caption"
                color={colorsPalettes.carbon[300]}
                data-automation="widget-card-subtitle"
            >
                {subtitle}
            </Text>
        ) : null}
    </Stack>
);

export interface IWidgetCardLayoutProps extends ITitleContentProps {
    actions?: ReactNode;
    automationIds?: {
        title?: string;
        body?: string;
    };
    className?: string;
    minHeight?: string;
}

export const WidgetCardLayout = ({
    actions,
    automationIds,
    children,
    minHeight,
    subtitle,
    title,
    tooltip,
    ...other
}: PropsWithChildren<IWidgetCardLayoutProps>) => (
    <Card
        {...other}
        flex="1"
        overflow="hidden"
        data-automation="widget-card"
    >
        <Card.Header withDivider>
            <Card.Title
                paddingY="16px"
                data-automation={automationIds?.title}
            >
                <TitleContent
                    subtitle={subtitle}
                    title={title}
                    tooltip={tooltip}
                />
            </Card.Title>
            {actions}
        </Card.Header>
        <Box
            minHeight={minHeight}
            data-automation={automationIds?.body}
        >
            {children}
        </Box>
    </Card>
);
